/**
 * Runtime ("virtual") .env overrides.
 *
 * `import.meta.env.*` values are inlined at build time and cannot be changed
 * without rebuilding. This layer lets a developer paste a `.env` snippet that is
 * stored in `localStorage` and read on top of the build-time values, so dev
 * tools and endpoints can be reconfigured at runtime (a reload applies them).
 *
 * The store is intentionally NOT user-scoped: it is read during app boot, before
 * authentication, so it lives under a single machine-level key.
 */

const STORAGE_KEY = 'virtual-env'

export type EnvKey =
  | 'VITE_APP_STATIC_URL'
  | 'VITE_APP_AI_API_BASE_URL'
  | 'VITE_APP_AIP_API_BASE_URL'
  | 'VITE_APP_GRAFANA_ORIGIN'
  | 'VITE_APP_USE_MSW_MOCKS'
  | 'VITE_APP_MOCK_TASK_TYPE'
  | 'VITE_APP_REACT_SCAN_DEVTOOLS'
  | 'VITE_APP_TANSTACK_ROUTER_DEVTOOLS'
  | 'VITE_APP_TANSTACK_QUERY_DEVTOOLS'

export type EnvOption = {
  key: EnvKey
  description: string
  example: string
}

export const ENV_OPTIONS: EnvOption[] = [
  {
    key: 'VITE_APP_USE_MSW_MOCKS',
    description:
      'Serve mocked API responses through MSW instead of calling the real backend. Options: true | false.',
    example: 'VITE_APP_USE_MSW_MOCKS=true',
  },
  {
    key: 'VITE_APP_MOCK_TASK_TYPE',
    description:
      'Which mock task the assign-task endpoint returns. Options: basic | basic_multi_asset | basic_image_form | basic_image_form_multi | basic_json_asset | basic_langfuse_trace | basic_langfuse_trace_preannotated | basic_span_labelling | video_annotation (any other value returns the default v2 basic task). Only used when mocks are enabled.',
    example: 'VITE_APP_MOCK_TASK_TYPE="basic_span_labelling"',
  },
  {
    key: 'VITE_APP_AI_API_BASE_URL',
    description:
      'AI backend (Python) base URL — any absolute URL. Overrides the URL derived from the current domain; only applied when VITE_APP_AIP_API_BASE_URL is also set.',
    example:
      'VITE_APP_AI_API_BASE_URL="https://dev-2.shared.ai.cloudfactory.app/ai/"',
  },
  {
    key: 'VITE_APP_AIP_API_BASE_URL',
    description:
      'AIP backend (Go) base URL — any absolute URL. Overrides the URL derived from the current domain; only applied when VITE_APP_AI_API_BASE_URL is also set.',
    example:
      'VITE_APP_AIP_API_BASE_URL="https://dev-2.shared.ai.cloudfactory.app/aip/"',
  },
  {
    key: 'VITE_APP_GRAFANA_ORIGIN',
    description:
      'Allowed Grafana origin for embedded analytics dashboard URLs.',
    example: 'VITE_APP_GRAFANA_ORIGIN="https://grafana.ai.cloudfactory.app"',
  },
  {
    key: 'VITE_APP_STATIC_URL',
    description:
      'Local routing base path, used on localhost. Options: empty for default, or a path string. The asset CDN base is fixed at build time, so at runtime this only affects the local base path.',
    example: 'VITE_APP_STATIC_URL=""',
  },
  {
    key: 'VITE_APP_REACT_SCAN_DEVTOOLS',
    description:
      'Enable the react-scan render profiler overlay. Options: true | false.',
    example: 'VITE_APP_REACT_SCAN_DEVTOOLS=false',
  },
  {
    key: 'VITE_APP_TANSTACK_ROUTER_DEVTOOLS',
    description:
      'Show the TanStack Router devtools panel. Options: true | false.',
    example: 'VITE_APP_TANSTACK_ROUTER_DEVTOOLS=false',
  },
  {
    key: 'VITE_APP_TANSTACK_QUERY_DEVTOOLS',
    description:
      'Show the TanStack Query devtools panel. Options: true | false.',
    example: 'VITE_APP_TANSTACK_QUERY_DEVTOOLS=false',
  },
]

const parseEnv = (raw: string): Record<string, string> => {
  const result: Record<string, string> = {}

  raw.split('\n').forEach((line) => {
    const trimmed = line.trim().replace(/^export\s+/, '')

    if (!trimmed || trimmed.startsWith('#')) {
      return
    }

    const separatorIndex = trimmed.indexOf('=')

    if (separatorIndex === -1) {
      return
    }

    const key = trimmed.slice(0, separatorIndex).trim()
    const value = trimmed
      .slice(separatorIndex + 1)
      .trim()
      .replace(/^["']|["']$/g, '')

    if (key) {
      result[key] = value
    }
  })

  return result
}

let cachedVirtualEnv: Record<string, string> | null = null

const getVirtualEnv = (): Record<string, string> => {
  if (!cachedVirtualEnv) {
    cachedVirtualEnv = parseEnv(localStorage.getItem(STORAGE_KEY) ?? '')
  }

  return cachedVirtualEnv
}

export const getEnv = (key: EnvKey): string | undefined => {
  const virtualEnv = getVirtualEnv()

  if (key in virtualEnv) {
    return virtualEnv[key]
  }

  return import.meta.env[key]
}

export const getRawVirtualEnv = (): string =>
  localStorage.getItem(STORAGE_KEY) ?? ''

export const setRawVirtualEnv = (raw: string): void => {
  const trimmed = raw.trim()

  if (trimmed) {
    localStorage.setItem(STORAGE_KEY, trimmed)
  } else {
    localStorage.removeItem(STORAGE_KEY)
  }

  cachedVirtualEnv = parseEnv(trimmed)
}
